import { noop } from '@aloft/toolkit-shared';

import { useCallback, useMemo, useRef } from 'react';

type UseEdgeTriggerOpts = {
  /**
   * The condition to monitor.
   */
  condition: boolean;

  /**
   * If provided, the action will be invoked when the condition becomes true
   * after the given delay has passed. If this is not provided it has the same
   * effect as providing a delay of 0 ms.
   */
  delayMs?: number;

  /**
   * The action to run.
   */
  action: () => void;
};

type UseEdgeTrigger = {
  /**
   * Restarts the delay interval, allowing it to be extended in response to
   * events.
   */
  restartTimer: () => void;
};

type UseEdgeTriggerState = {
  previous: boolean;
  timer?: number;
  delayMs: number;
  action: () => void;
};

/**
 * Invokes a callback when a condition becomes true, optionally after a delay.
 * If the condition becomes false during the delay period, the callback is not
 * run. The callback is never invoked synchronously, so you can treat it the
 * same as a button press or an API callback. The condition is considered to be
 * false initially, so if it's true on the initial render then this is
 * considered to be a rising edge.
 */
export function useEdgeTrigger(opts: UseEdgeTriggerOpts): UseEdgeTrigger {
  const ref = useRef<UseEdgeTriggerState>({
    previous: false,
    delayMs: opts.delayMs ?? 0,
    action: noop,
  });

  const restartTimer = useCallback(() => {
    if (!ref.current.previous) return;
    window.clearTimeout(ref.current.timer);
    ref.current.timer = window.setTimeout(() => ref.current.action(), ref.current.delayMs);
  }, []);
  const cancelTimer = useCallback(() => {
    window.clearTimeout(ref.current.timer);
  }, []);

  const res: UseEdgeTrigger = useMemo(() => ({ restartTimer }), [restartTimer]);

  function handleRisingEdge() {
    ref.current.previous = true;
    restartTimer();
  }
  function handleFallingEdge() {
    ref.current.previous = false;
    cancelTimer();
  }

  ref.current.delayMs = opts.delayMs ?? 0;
  ref.current.action = opts.action;

  if (opts.condition && !ref.current.previous) handleRisingEdge();
  else if (!opts.condition && ref.current.previous) handleFallingEdge();

  return res;
}
